import { useState, useEffect, useMemo } from 'react';
import { getExtractionFieldsByRulesetType } from '../services/api/rulesetApi';

/**
 * Computes available field suggestions for rule configuration inputs.
 *
 * @param {number|null} extractionRulesetTypeId - Selected ruleset type (null = no extraction fields)
 * @param {number} executionOrder - Current rule's execution order
 * @param {Array} allRules - All rules for the customer (from parent)
 * @param {number|null} currentRuleId - ID of rule being edited (to exclude from preceding)
 * @returns {{ availableFields: Array, loading: boolean }}
 */
export const useAvailableFields = (extractionRulesetTypeId, executionOrder, allRules, currentRuleId) => {
  const [extractionFields, setExtractionFields] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch extraction fields when ruleset type changes
  useEffect(() => {
    if (!extractionRulesetTypeId) {
      setExtractionFields([]);
      return;
    }

    let cancelled = false;
    const fetchFields = async () => {
      setLoading(true);
      try {
        const response = await getExtractionFieldsByRulesetType(extractionRulesetTypeId);
        if (!cancelled) {
          setExtractionFields(response.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch extraction fields for autocomplete:', err);
        if (!cancelled) {
          setExtractionFields([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchFields();
    return () => { cancelled = true; };
  }, [extractionRulesetTypeId]);

  // Compute preceding rule target fields
  const precedingRuleFields = useMemo(() => {
    if (!allRules || allRules.length === 0 || !executionOrder) return [];

    const preceding = allRules.filter(rule =>
      rule.executionOrder < executionOrder &&
      rule.isActive &&
      rule.id !== currentRuleId &&
      (rule.extractionRulesetTypeId === extractionRulesetTypeId ||
       rule.extractionRulesetTypeId === null ||
       extractionRulesetTypeId === null)
    );

    const fields = [];
    const seen = new Set();

    for (const rule of preceding) {
      try {
        const config = typeof rule.configurationJson === 'string'
          ? JSON.parse(rule.configurationJson)
          : rule.configurationJson;

        if (!config) {
          continue;
        }

        const targetFields = extractTargetFields(rule.ruleType, config);

        for (const fieldName of targetFields) {
          if (fieldName && !seen.has(fieldName.toLowerCase())) {
            seen.add(fieldName.toLowerCase());
            fields.push({
              value: fieldName,
              label: fieldName,
              category: 'preceding-rule',
              fieldType: null,
              source: rule.name
            });
          }
        }
      } catch (err) {
        console.warn(`Could not parse config for rule ${rule.name}:`, err);
      }
    }

    return fields;
  }, [allRules, executionOrder, currentRuleId, extractionRulesetTypeId]);

  // Combine extraction fields + preceding rule fields (deduplicated)
  const availableFields = useMemo(() => {
    const extractionSuggestions = extractionFields.map(field => ({
      value: field.fieldKey,
      label: field.fieldKey,
      category: 'extraction',
      fieldType: field.fieldType === 0 ? 'Header' : 'Detail',
      source: field.name || field.displayName
    }));

    // Deduplicate: extraction fields take precedence
    const extractionKeys = new Set(extractionSuggestions.map(f => f.value.toLowerCase()));
    const uniquePrecedingFields = precedingRuleFields.filter(
      f => !extractionKeys.has(f.value.toLowerCase())
    );

    return [...extractionSuggestions, ...uniquePrecedingFields];
  }, [extractionFields, precedingRuleFields]);

  return { availableFields, loading };
};

/**
 * Extracts target field names from a rule's parsed configuration.
 */
function extractTargetFields(ruleType, config) {
  const fields = [];

  if (!config) {
    return fields;
  }

  switch (ruleType?.toUpperCase()) {
    case 'STRING_MANIPULATION':
      if (config.targetField) fields.push(config.targetField);
      break;

    case 'CONDITIONAL':
      if (config.targetField) fields.push(config.targetField);
      break;

    case 'LOOKUP':
      if (Array.isArray(config.returnFields)) {
        for (const rf of config.returnFields) {
          if (rf.targetField) fields.push(rf.targetField);
        }
      }
      break;

    default:
      break;
  }

  return fields;
}
